/**
 * Comando Ops — AI Barcode / Tracking Scanner
 *
 * Fetches an existing package photo from Supabase Storage
 * and sends it to Claude Vision via swift-endpoint to
 * extract the tracking number from the shipping label.
 *
 * Returns: { success: true, tracking_number, carrier, confidence }
 *      or: { success: false, error: string }
 */
export async function scanBarcode(imageUrl) {
  const supabaseUrl     = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase config missing');
  }

  // Fetch the image from Supabase Storage public URL
  const imgResponse = await fetch(imageUrl);
  if (!imgResponse.ok) {
    throw new Error('No se pudo cargar la foto del paquete');
  }
  const blob      = await imgResponse.blob();
  const base64    = await blobToBase64(blob);
  const mediaType = blob.type || 'image/jpeg';

  console.log('[scanBarcode] Sending package photo to AI — size:', blob.size);

  const response = await fetch(
    `${supabaseUrl}/functions/v1/swift-endpoint`,
    {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        base64,
        mediaType,
        promptOverride: `Eres un lector de etiquetas de envío para una empresa logística.

Mira esta foto de un paquete o etiqueta de envío.
Tu tarea es encontrar el NÚMERO DE RASTREO PRINCIPAL.

CÓMO IDENTIFICAR EL NÚMERO DE RASTREO:
1. Encuentra el código de barras 1D principal (líneas verticales de ancho variable)
2. El número de rastreo es la cadena alfanumérica impresa en TEXTO GRANDE directamente debajo de ese código de barras
3. Lee cada carácter exactamente como está impreso

PREFIJOS DE TRANSPORTISTA:
- UPS:              1Z seguido de 16 caracteres     ej: 1Z999AA10123456784
- USPS:             9 seguido de 21 dígitos         ej: 9400111899223397765910
- FedEx:            12 o 15 dígitos                 ej: 774899172137
- Amazon:           TBA seguido de números          ej: TBA123456789000
- OnTrac/LaserShip: GFUS seguido de caracteres     ej: GFUS01046113264706
- SpeedX (SHEIN):   SPX seguido de caracteres      ej: SPXBWI0056726041300572310
- DHL:              JD seguido de caracteres        ej: JD014600006281350443
- LSO:              1LS seguido de caracteres

NO RETORNES estos:
- Códigos postales (5 dígitos)
- Pesos (contienen "lb" u "oz")
- Códigos de ruta cortos (EWR, BWI)
- Números de referencia ("Reference:" o que empiecen con "C2...")
- Contenido de códigos QR
- Números escritos a mano

INSTRUCCIÓN CRÍTICA: Responde SOLO con el objeto JSON. Sin texto antes ni después.
NO expliques. NO describas. EMPIEZA con { y TERMINA con }.

{"tracking_number": "SPXBWI0056726041300572310", "carrier": "SpeedX", "confidence": 0.98}

Si no hay número visible: {"tracking_number": null, "carrier": "Unknown", "confidence": 0}
Nunca inventes un número.`,
      }),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error('Error del servicio AI: ' + (err.error || response.status));
  }

  const data = await response.json();
  console.log('[scanBarcode] Raw response:', JSON.stringify(data).slice(0, 300));

  if (data.error) {
    return { success: false, error: 'Error del servicio: ' + data.error };
  }

  const content   = data.content || [];
  const textBlock = content.find(b => b.type === 'text');
  if (!textBlock || !textBlock.text) {
    return { success: false, error: 'Respuesta vacía del AI' };
  }

  const raw   = textBlock.text;
  console.log('[scanBarcode] Claude text:', raw);

  // Strip markdown fences and extract JSON object
  const clean = raw.replace(/```json\s*/gi, '').replace(/```\s*/gi, '').trim();

  // Claude sometimes prepends explanation before the JSON.
  // Find all simple {...} blocks and pick the last one that has tracking_number.
  let result = null;
  const blockRe = /\{[^{}]+\}/g;
  const blocks = [...clean.matchAll(blockRe)];
  for (let i = blocks.length - 1; i >= 0; i--) {
    try {
      const parsed = JSON.parse(blocks[i][0]);
      if ('tracking_number' in parsed) { result = parsed; break; }
    } catch (e) { continue; }
  }

  // Fallback: grab from last { to last }
  if (!result) {
    const a = clean.lastIndexOf('{');
    const b = clean.lastIndexOf('}');
    if (a !== -1 && b > a) {
      try {
        const parsed = JSON.parse(clean.slice(a, b + 1));
        if ('tracking_number' in parsed) result = parsed;
      } catch (e) { /* no match */ }
    }
  }

  if (!result) {
    console.error('[scanBarcode] No JSON found. Raw:', clean.slice(0, 300));
    return { success: false, error: 'No se encontró tracking. Intente con una foto más clara.' };
  }

  try {
    console.log('[scanBarcode] Parsed:', result);

    if (!result.tracking_number) {
      return {
        success: false,
        error: 'No se encontró tracking en la foto. Intente con una foto más clara.',
      };
    }

    return {
      success:         true,
      tracking_number: result.tracking_number,
      carrier:         result.carrier  || 'Unknown',
      confidence:      result.confidence || 0,
    };
  } catch (e) {
    console.error('[scanBarcode] JSON parse failed. Text was:', clean);
    return { success: false, error: 'No se pudo leer la respuesta del AI — intente de nuevo' };
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader   = new FileReader();
    reader.onload  = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}